import React from 'react';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { AuthLayout } from './AuthLayout';
import { createLogger } from '../../lib/logger';

const log = createLogger('auth');

interface ProtectedRouteProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  requireAuth?: boolean;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  fallback,
  requireAuth = true 
}) => {
  const { user, loading } = useAuth();
  log.debug('ProtectedRoute: 渲染 - user:', user?.id ? '已登入' : '未登入', 'loading:', loading);

  // 開發模式：檢查是否需要繞過認證 (暫時註釋以解決 TypeScript 錯誤)
  // const isDev = false; // import.meta.env.DEV;
  const bypassAuth = false; // isDev && localStorage.getItem('dev_bypass_auth') === 'true';

  // 載入中狀態
  if (loading && !bypassAuth) {
    log.debug('ProtectedRoute: 顯示載入中畫面');
    return fallback || (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-wedding-action mx-auto mb-4" />
          <p className="text-gray-600">載入中...</p>
        </div>
      </div>
    );
  }

  // 開發模式繞過認證
  if (bypassAuth) {
    return <>{children}</>;
  }

  // 需要認證但用戶未登入
  if (requireAuth && !user) {
    log.debug('ProtectedRoute: 需要認證但用戶未登入，顯示登入畫面');
    return (
      <AuthLayout
        onSuccess={() => {
          // 成功登入後等待 AuthContext 狀態更新，無需手動重載頁面
          log.info('ProtectedRoute: 登入成功，等待狀態更新');
          // AuthContext 的 user 狀態變化會自動觸發 ProtectedRoute 重新渲染
        }}
      />
    );
  }

  // 不需要認證或用戶已登入
  log.debug('ProtectedRoute: 用戶已登入或不需要認證，顯示子組件');
  return <>{children}</>;
};

// 載入中組件
export const LoadingSpinner: React.FC<{ message?: string }> = ({ message = '載入中...' }) => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <Loader2 className="w-8 h-8 animate-spin text-wedding-action mx-auto mb-4" />
      <p className="text-gray-600">{message}</p>
    </div>
  </div>
);

// 未認證組件
export const UnauthenticatedFallback: React.FC<{ onAuthSuccess?: () => void }> = ({ onAuthSuccess }) => (
  <AuthLayout onSuccess={onAuthSuccess} />
);